import { Button, Input, InputWrapperProps, TextInput, TextInputProps } from "@mantine/core";
import { useFormHelperContext } from "../form";
import { useController, useFieldArray } from "react-hook-form";
import { InputWrapper } from "../inputWrapper";
import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

export interface FieldInputTextProps extends TextInputProps {
    label: string;
    name: string;
    inputWrapper?: InputWrapperProps;
}

export const FieldInputText: React.FC<FieldInputTextProps> = ({ label, name, placeholder, inputWrapper, ...rest }) => {
    const { register, control } = useFormHelperContext();
    const { field, fieldState: { error }, } = useController({ name, control });


    if (!register) {
        throw new Error("Field.Text must be used within a FormHelper component");
    }

    return (
        <InputWrapper label={label} {...inputWrapper} withAsterisk={rest.withAsterisk}>
            <div className="flex flex-col w-full">
                <TextInput
                    {...field}
                    value={field.value ?? ""}
                    placeholder={placeholder}
                    data-testid={'field-text-' + name}
                    error={
                        error ? " " : undefined
                    }
                    {...rest}
                />
                {error && (
                    <Input.Error>{error.message}</Input.Error>
                )}
            </div>
        </InputWrapper>

    );
};


export const MultiFieldInputText: React.FC<FieldInputTextProps> = ({ label, name, placeholder, inputWrapper, ...rest }) => {
    const { register, control, formState } = useFormHelperContext();

    const { t } = useTranslation('generalMessage')

    const errors = formState.errors
    const { fields, append, remove } = useFieldArray({ control, name });

    if (!register) {
        throw new Error("Field.MultiText must be used within a FormHelper component");
    }

    return (
        <InputWrapper label={label} {...inputWrapper} withAsterisk={rest.withAsterisk}>
            <div className="flex flex-col w-full" key={'multi-' + name}>
                {fields.map((field, index) => {
                    const errIndex = (errors[name] as any)?.[index]
                    return (
                        <div key={field.id}>
                            <div className="flex flex-grow w-full">
                                <TextInput
                                    key={field.id}
                                    className="flex-grow my-2"
                                    error={
                                        errIndex ? " " : undefined
                                    }
                                    {...rest}
                                    {...register(`${name}.${index}`, { required: "Name is required" })}
                                />
                                <Button variant="outline" onClick={() => remove(index)} color="red" className="m-auto ml-2"> <IconTrash size={12} /></Button>
                            </div>

                            {errIndex && (
                                <Input.Error>{errIndex.message}</Input.Error>
                            )}

                        </div>

                    )
                })}
                <Button variant="light" onClick={() => append("")}>{t('addMore')}</Button>
            </div>
        </InputWrapper>

    );
};
